import React from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Words, FadeUp, PopIn, EASE } from "@/components/Reveal";
import PillButton from "@/components/PillButton";
import EpisodeCard from "@/components/EpisodeCard";
import { getEpisode, relatedEpisodes } from "@/data/episodes";
import { ORGS } from "@/data/site";
import NotFound from "@/pages/NotFound";

const EpisodeDetail = () => {
  const { id } = useParams();
  const ep = getEpisode(id);
  if (!ep) return <NotFound />;
  const related = relatedEpisodes(ep);

  return (
    <main className="bg-white">
      {/* Hero */}
      <section className="w-full bg-white pt-[110px] sm:pt-[130px] lg:pt-[150px]">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-[56px]">
          <div className="mx-auto grid max-w-[1208px] grid-cols-1 items-center gap-12 lg:grid-cols-[minmax(0,1fr)_380px] lg:gap-16 lg:px-[60px]">
            <div className="flex flex-col gap-5">
              <FadeUp mode="load" y={12}>
                <Link to="/episodes" data-testid="episode-back-link" className="group/link inline-flex items-center gap-1 font-display text-[13px] uppercase tracking-[0.18em] text-charcoal transition-colors hover:text-black">
                  <span aria-hidden="true" className="transition-transform duration-500 group-hover/link:-translate-x-1">←</span> All Episodes
                </Link>
              </FadeUp>
              <PopIn mode="load">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center rounded-full bg-linen px-3 py-1 text-[11px] uppercase leading-[16px] tracking-[0.22em] text-black font-display">
                    Episode {ep.number}
                  </span>
                  <span className="inline-flex items-center rounded-full border border-silver bg-white px-3 py-1 text-[11px] uppercase leading-[16px] tracking-[0.22em] text-charcoal font-display">
                    {ep.track}
                  </span>
                  {ep.comingSoon && (
                    <span className="inline-flex items-center rounded-full border border-hairline bg-white-smoke px-3 py-1 text-[11px] uppercase leading-[16px] tracking-[0.22em] text-charcoal-mid font-display">
                      Coming Soon
                    </span>
                  )}
                </div>
              </PopIn>
              <h1 className="font-display text-[36px] leading-[1.08] text-black sm:text-[48px] lg:text-[56px] lg:leading-[62px]" aria-label={ep.title}>
                <Words text={ep.title} mode="load" delay={0.25} stagger={0.06} />
              </h1>
              <FadeUp mode="load" delay={0.5}>
                <p className="max-w-[600px] text-[15px] leading-[26px] text-charcoal sm:text-[15.6px] sm:leading-[27px]">{ep.description}</p>
              </FadeUp>
              <FadeUp mode="load" delay={0.6}>
                <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-4 border-t border-hairline pt-5 sm:grid-cols-4">
                  {[
                    ["Release", ep.date],
                    ["Runtime", ep.duration],
                    ["Format", ep.type],
                    ["Track", ep.track],
                  ].map(([k, v]) => (
                    <div key={k} className="flex flex-col gap-1">
                      <span className="eyebrow text-[10.5px] leading-[16px] text-charcoal-mid">{k}</span>
                      <span className="font-display text-[14px] leading-[20px] text-black sm:text-[14.5px]">{v}</span>
                    </div>
                  ))}
                </div>
              </FadeUp>
              <FadeUp mode="load" delay={0.7} className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-center">
                <PillButton href="/community" dataTestId="episode-join-button">
                  Join the Community
                </PillButton>
                <PillButton href="/episodes" variant="outline" dataTestId="episode-listen-button">
                  Listen
                </PillButton>
              </FadeUp>
            </div>
            <FadeUp mode="load" y={30} className="group relative mx-auto aspect-square w-full max-w-[380px] overflow-hidden rounded-full ring-1 ring-hairline lg:mx-0 lg:w-full">
              <div className="absolute inset-0 transition-transform duration-[1200ms] group-hover:scale-[1.05]" style={{ transitionTimingFunction: "cubic-bezier(0.22,1,0.36,1)" }}>
                <img alt={`${ep.title} artwork`} className="h-full w-full object-cover" src="/figma/logomark.jpg" />
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Show notes */}
      <section id="show-notes" className="w-full bg-white-smoke py-16 sm:py-20 lg:py-[88px]">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-[56px]">
          <div className="mx-auto flex max-w-[1208px] flex-col gap-10 lg:px-[60px]">
            <h2 className="font-display text-[30px] leading-[1.15] text-black sm:text-[36px] lg:text-[42px]">
              <Words text="Show Notes" />
            </h2>
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-14">
              <div className="flex flex-col gap-6">
                <FadeUp y={14}>
                  <h3 className="font-display text-[22px] leading-[1.2] text-black sm:text-[26px]">What this episode covers</h3>
                </FadeUp>
                <div className="flex flex-col divide-y divide-silver-line">
                  {ep.showNotes.covers.map((c, i) => (
                    <FadeUp key={c} delay={0.06 * i} y={14}>
                      <div className="flex items-start gap-5 py-5" data-testid={`episode-cover-point-${i + 1}`}>
                        <span className="font-display text-[13px] uppercase tracking-[0.22em] text-charcoal-mid">0{i + 1}</span>
                        <p className="max-w-[460px] text-[15px] leading-[26px] text-charcoal-dark">{c}</p>
                      </div>
                    </FadeUp>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-6">
                <FadeUp y={14}>
                  <h3 className="font-display text-[22px] leading-[1.2] text-black sm:text-[26px]">Episode Sponsors</h3>
                </FadeUp>
                {ep.showNotes.sponsors.length > 0 ? (
                  <div className="flex flex-col gap-4">
                    {ep.showNotes.sponsors.map((s, i) => (
                      <FadeUp key={s} delay={0.06 * i} y={14}>
                        <div data-testid={`episode-sponsor-${i + 1}`} className="rounded-2xl border border-hairline bg-white p-6 transition-all duration-500 hover:-translate-y-0.5 hover:border-silver">
                          <p className="font-display text-[19px] leading-[26px] text-black">{s}</p>
                          <p className="mt-2 text-[13.8px] leading-[24px] text-charcoal">{ORGS[s]}</p>
                        </div>
                      </FadeUp>
                    ))}
                  </div>
                ) : (
                  <FadeUp y={14}>
                    <p className="text-[14.6px] leading-[26px] text-charcoal">No sponsors this episode — the podcast stays free, and so does the table.</p>
                  </FadeUp>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related episodes */}
      <section className="w-full bg-white py-16 sm:py-20 lg:py-[88px]">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-[56px]">
          <div className="mx-auto flex max-w-[1208px] flex-col gap-10 lg:px-[60px]">
            <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
              <h2 className="font-display text-[30px] leading-[1.15] text-black sm:text-[36px] lg:text-[42px]">
                <Words text="Keep Listening" />
              </h2>
              <FadeUp y={10}>
                <Link to="/episodes" data-testid="episode-related-library-link" className="group/link inline-flex items-center font-display text-[15px] leading-[25.6px] text-charcoal sm:text-[16px]">
                  <span className="transition-transform duration-500 ease-out group-hover/link:-translate-x-1">Episode library</span>
                  <span aria-hidden="true" className="ml-1 transition-transform duration-500 ease-out group-hover/link:translate-x-1">→</span>
                </Link>
              </FadeUp>
            </div>
            <div className="grid grid-cols-1 gap-7 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((rel, i) => (
                <EpisodeCard key={rel.id} episode={rel} index={i} />
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default EpisodeDetail;
